"use client";

import { Check } from "lucide-react";
import Link from "next/link";

import {
  getStateCardTestId,
  stateSelectorDataTestIds,
} from "@/lib/data-test-ids/state-selector.data-test-ids";
import routes from "@/lib/routes";
import type { StateConfig } from "@/lib/states-config";
import { cn } from "@/lib/utils";

export interface StateCardProps {
  state: StateConfig;
  subtitle: string;
  isSelected: boolean;
  onClick?: () => void;
}

export function StateCard({
  state,
  subtitle,
  isSelected,
  onClick,
}: StateCardProps) {
  const { route, title, abbreviation } = state;

  return (
    <Link
      href={routes.getJobsState({ state })}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 px-3 py-3 rounded-xl border transition-all duration-200 ease-out cursor-pointer group",
        isSelected
          ? "border-surface-inverted bg-surface-subtle ring-1 ring-surface-inverted/10"
          : "border-line-faint bg-card hover:bg-surface-subtle/80 hover:border-line-strong hover:shadow-sm",
      )}
      data-testid={getStateCardTestId(route)}
    >
      <StateAbbreviationBadge
        abbreviation={abbreviation}
        isSelected={isSelected}
      />

      <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
        <StateInfo title={title} subtitle={subtitle} isSelected={isSelected} />
        {isSelected && <SelectedIndicator />}
      </div>
    </Link>
  );
}

interface StateAbbreviationBadgeProps {
  abbreviation: string;
  isSelected: boolean;
}

function StateAbbreviationBadge({
  abbreviation,
  isSelected,
}: StateAbbreviationBadgeProps) {
  return (
    <div
      className={cn(
        "w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center",
        "text-[10px] sm:text-xs font-bold tracking-wide",
        "transition-all duration-200 flex-shrink-0",
        isSelected
          ? "bg-surface-inverted text-primary-foreground"
          : "bg-gradient-to-br from-surface-subtle to-surface-strong text-muted-foreground ring-1 ring-surface-strong/50 group-hover:from-surface-strong group-hover:to-surface-subtle group-hover:text-foreground group-hover:ring-line-strong",
      )}
      data-testid={stateSelectorDataTestIds.STATE_ABBREVIATION}
    >
      {abbreviation}
    </div>
  );
}

interface StateInfoProps {
  title: string;
  subtitle: string;
  isSelected: boolean;
}

function StateInfo({ title, subtitle, isSelected }: StateInfoProps) {
  return (
    <div className="flex flex-col">
      <span
        className={cn(
          "font-semibold text-sm",
          isSelected ? "text-foreground font-bold" : "text-foreground",
        )}
        data-testid={stateSelectorDataTestIds.STATE_NAME}
      >
        {title}
      </span>
      <span
        className="text-xs text-ink-subtle"
        data-testid={stateSelectorDataTestIds.STATE_SUBTITLE}
      >
        {subtitle}
      </span>
    </div>
  );
}

function SelectedIndicator() {
  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <Check className="w-5 h-5 text-foreground" />
    </div>
  );
}
